import { useEffect, useState } from 'react';
import type { CrudFilter } from '@refinedev/core';
import { InputNumber, Select } from 'antd';
import { FilterPanel } from '@/components/common/FilterPanel';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type ActivitySubjectFilterProps = {
  /** Model type field, applied as `[eq]` (e.g. `object_type`). */
  typeField: string;
  /** Model id field, applied as `[eq]` (e.g. `object_id`). */
  idField: string;
  options: { label: string; value: string }[];
  typePlaceholder?: string;
  idPlaceholder?: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
};

/** A filter restored from the URL carries the id as a string. */
const toId = (value: unknown): number | null =>
  value == null || value === '' ? null : Number(value);

/**
 * Type + id column filter for an activity's subject or target. Ids repeat
 * across tables, so the id input stays disabled until a type is picked, and
 * clearing the type drops the id with it.
 *
 * Writes through `setFilters` rather than antd's own filter state — see
 * `useAdminTable` for why the two must not be mixed.
 */
export const ActivitySubjectFilter = ({
  typeField,
  idField,
  options,
  typePlaceholder,
  idPlaceholder,
  filters,
  confirm,
  clearFilters,
  setFilters,
}: ActivitySubjectFilterProps) => {
  const currentType = getFilterValue<string>(filters, typeField, 'eq');
  const currentId = toId(getFilterValue(filters, idField, 'eq'));
  const [type, setType] = useState<string | undefined>(currentType);
  const [id, setId] = useState<number | null>(currentId);

  useEffect(() => setType(currentType), [currentType]);
  useEffect(() => setId(currentId), [currentId]);

  const apply = (nextType?: string, nextId?: number | null) =>
    setFilters(
      [
        // A multi-select `[in]` from before this filter may linger in a URL.
        { field: typeField, operator: 'in', value: undefined },
        { field: typeField, operator: 'eq', value: nextType },
        {
          field: idField,
          operator: 'eq',
          value: nextType ? (nextId ?? undefined) : undefined,
        },
      ],
      'merge'
    );

  const handleFilter = () => {
    apply(type, id);
    confirm();
  };

  const handleReset = () => {
    setType(undefined);
    setId(null);
    apply(undefined, undefined);
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <Select<string>
        allowClear
        showSearch
        placeholder={typePlaceholder}
        style={{ minWidth: 240 }}
        value={type}
        onChange={(value?: string) => {
          setType(value);
          if (!value) setId(null);
        }}
        options={options}
      />
      <InputNumber<number>
        min={1}
        precision={0}
        controls={false}
        disabled={!type}
        placeholder={idPlaceholder}
        style={{ width: '100%' }}
        value={id}
        onChange={(value) => setId(value)}
        onPressEnter={handleFilter}
      />
    </FilterPanel>
  );
};
